/* ============================================
   NODUS 01 · PRODUCT
   Self-contained add-to-cart that can post the main variant and any
   checked paid add-on products in a single /cart/add.js request.
   ============================================ */

if (!customElements.get('nodus-product-gallery')) {
	class NodusProductGallery extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('[data-nodus-gallery-track]');
			this.thumbList = this.querySelector('[data-nodus-thumbs]');

			if (!this.track || !this.thumbList) return;

			this.slides = Array.from(this.track.querySelectorAll('.nodus-product__shot'));
			this.thumbs = Array.from(this.thumbList.querySelectorAll('[data-nodus-thumb]'));

			if (this.slides.length < 2) return;

			this.handleThumbClick = this.onThumbClick.bind(this);
			this.handleScroll = this.onScroll.bind(this);

			this.thumbList.addEventListener('click', this.handleThumbClick);
			this.track.addEventListener('scroll', this.handleScroll, { passive: true });
		}

		disconnectedCallback() {
			if (this.thumbList && this.handleThumbClick) {
				this.thumbList.removeEventListener('click', this.handleThumbClick);
			}

			if (this.track && this.handleScroll) {
				this.track.removeEventListener('scroll', this.handleScroll);
			}
		}

		onThumbClick(event) {
			const thumb = event.target.closest('[data-nodus-thumb]');

			if (!thumb) return;

			const index = Number(thumb.dataset.nodusThumb);
			const slide = this.slides[index];

			if (!slide) return;

			// Slides share an offset parent, so the delta against the first one is the scroll target.
			this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
			this.setActive(index);
		}

		onScroll() {
			if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

			this.scrollFrame = requestAnimationFrame(() => {
				const origin = this.slides[0].offsetLeft + this.track.scrollLeft;
				let closest = 0;
				let smallest = Infinity;

				for (const [index, slide] of this.slides.entries()) {
					const distance = Math.abs(slide.offsetLeft - origin);

					if (distance < smallest) {
						smallest = distance;
						closest = index;
					}
				}

				this.setActive(closest);
			});
		}

		setActive(index) {
			for (const [position, thumb] of this.thumbs.entries()) {
				if (position === index) {
					thumb.setAttribute('aria-current', 'true');
				} else {
					thumb.removeAttribute('aria-current');
				}
			}
		}
	}

	customElements.define('nodus-product-gallery', NodusProductGallery);
}

if (!customElements.get('nodus-variant-picker')) {
	class NodusVariantPicker extends HTMLElement {
		connectedCallback() {
			const dataScript = this.querySelector('[data-nodus-variant-data]');

			this.variants = dataScript ? JSON.parse(dataScript.textContent) : [];
			this.handleChange = this.onChange.bind(this);
			this.addEventListener('change', this.handleChange);
		}

		disconnectedCallback() {
			if (this.handleChange) this.removeEventListener('change', this.handleChange);
		}

		get selectedOptions() {
			const checked = this.querySelectorAll('input[type="radio"]:checked');

			return Array.from(checked).map((input) => input.value);
		}

		onChange() {
			const selected = this.selectedOptions;
			const match = this.variants.find(
				(variant) =>
					variant.options.length === selected.length &&
					variant.options.every((value, index) => value === selected[index])
			);

			this.dispatchEvent(
				new CustomEvent('nodus:variant-change', {
					bubbles: true,
					detail: { variant: match || null },
				})
			);
		}
	}

	customElements.define('nodus-variant-picker', NodusVariantPicker);
}

if (!customElements.get('nodus-product-form')) {
	class NodusProductForm extends HTMLElement {
		connectedCallback() {
			this.form = this.querySelector('form');

			if (!this.form) return;

			this.section = this.closest('[data-nodus-product]');
			this.errorBox = this.querySelector('[data-nodus-error]');
			this.submitButton = this.querySelector('[data-nodus-submit]');
			this.submitText = this.querySelector('[data-nodus-submit-text]');
			this.buyNowButton = this.querySelector('[data-nodus-buy-now]');
			this.variantInput = this.querySelector('[data-nodus-variant-id]');
			this.quantityInput = this.querySelector('[data-nodus-quantity]');
			this.stickyButton = this.section ? this.section.querySelector('[data-nodus-sticky-submit]') : null;
			this.stickyBar = this.section ? this.section.querySelector('[data-nodus-sticky]') : null;
			this.priceTarget = this.section ? this.section.querySelector('[data-nodus-price]') : null;
			this.compareTarget = this.section ? this.section.querySelector('[data-nodus-compare]') : null;
			this.stickyPriceTarget = this.section ? this.section.querySelector('[data-nodus-sticky-price]') : null;

			this.handleSubmit = this.onSubmit.bind(this);
			this.handleBuyNow = this.onBuyNow.bind(this);
			this.handleAddonToggle = this.onAddonToggle.bind(this);
			this.handleVariantChange = this.onVariantChange.bind(this);

			this.form.addEventListener('submit', this.handleSubmit);
			this.addEventListener('change', this.handleAddonToggle);
			this.addEventListener('nodus:variant-change', this.handleVariantChange);

			if (this.buyNowButton) this.buyNowButton.addEventListener('click', this.handleBuyNow);

			if (this.stickyBar) document.body.classList.add('nodus-product-sticky-active');

			if (this.submitText) this.submitText.dataset.defaultText = this.submitText.textContent.trim();

			this.syncAddonFields();
		}

		disconnectedCallback() {
			if (this.form && this.handleSubmit) this.form.removeEventListener('submit', this.handleSubmit);

			if (this.handleAddonToggle) this.removeEventListener('change', this.handleAddonToggle);

			if (this.handleVariantChange) this.removeEventListener('nodus:variant-change', this.handleVariantChange);

			if (this.buyNowButton && this.handleBuyNow) {
				this.buyNowButton.removeEventListener('click', this.handleBuyNow);
			}

			document.body.classList.remove('nodus-product-sticky-active');
		}

		get cartDrawer() {
			return document.querySelector('cart-drawer');
		}

		onAddonToggle(event) {
			if (event.target.matches('[data-nodus-addon]')) this.syncAddonFields();
		}

		syncAddonFields() {
			for (const addon of this.querySelectorAll('.nodus-product__addon')) {
				const checkbox = addon.querySelector('[data-nodus-addon]');

				if (!checkbox) continue;

				for (const field of addon.querySelectorAll('[data-nodus-addon-field]')) {
					field.disabled = !checkbox.checked;
				}
			}
		}

		onVariantChange(event) {
			const variant = event.detail.variant;

			if (!variant) {
				this.setUnavailable(window.variantStrings ? window.variantStrings.unavailable : '');
				return;
			}

			if (this.variantInput) this.variantInput.value = variant.id;

			if (this.priceTarget) this.priceTarget.textContent = variant.price;

			if (this.stickyPriceTarget) this.stickyPriceTarget.textContent = variant.price;

			if (this.compareTarget) {
				this.compareTarget.textContent = variant.on_sale ? variant.compare_at_price : '';
				this.compareTarget.hidden = !variant.on_sale;
			}

			if (variant.available) {
				this.setAvailable();
			} else {
				this.setUnavailable(window.variantStrings ? window.variantStrings.soldOut : '');
			}
		}

		setAvailable() {
			for (const button of [this.submitButton, this.buyNowButton, this.stickyButton]) {
				if (button) button.disabled = false;
			}

			if (this.submitText && this.submitText.dataset.defaultText) {
				this.submitText.textContent = this.submitText.dataset.defaultText;
			}
		}

		setUnavailable(label) {
			for (const button of [this.submitButton, this.buyNowButton, this.stickyButton]) {
				if (button) button.disabled = true;
			}

			if (this.submitText && label) {
				if (!this.submitText.dataset.defaultText) {
					this.submitText.dataset.defaultText = this.submitText.textContent.trim();
				}

				this.submitText.textContent = label;
			}
		}

		buildItems() {
			const variantId = this.variantInput ? Number(this.variantInput.value) : null;

			if (!variantId) return null;

			const quantity = this.quantityInput ? Math.max(1, Number(this.quantityInput.value) || 1) : 1;
			const properties = {};
			const items = [];

			for (const addon of this.querySelectorAll('.nodus-product__addon')) {
				const checkbox = addon.querySelector('[data-nodus-addon]');

				if (!checkbox || !checkbox.checked) continue;

				const addonVariantId = Number(checkbox.dataset.nodusAddonVariant);

				if (addonVariantId) items.push({ id: addonVariantId, quantity: 1 });

				for (const field of addon.querySelectorAll('[data-nodus-addon-field]')) {
					const key = field.dataset.nodusProperty;
					const value = field.value.trim();

					if (key && value) properties[key] = value;
				}
			}

			const mainItem = { id: variantId, quantity };

			if (Object.keys(properties).length > 0) mainItem.properties = properties;

			items.unshift(mainItem);

			return items;
		}

		setLoading(state) {
			for (const button of [this.submitButton, this.buyNowButton, this.stickyButton]) {
				if (!button) continue;

				button.classList.toggle('is-loading', state);
				button.setAttribute('aria-busy', state ? 'true' : 'false');
			}
		}

		showError(message) {
			if (!this.errorBox) return;

			this.errorBox.textContent = message;
			this.errorBox.hidden = !message;
		}

		onSubmit(event) {
			event.preventDefault();
			this.addToCart(false);
		}

		onBuyNow(event) {
			event.preventDefault();
			this.addToCart(true);
		}

		async addToCart(goToCheckout) {
			const items = this.buildItems();

			if (!items) {
				this.showError(window.cartStrings ? window.cartStrings.error : '');
				return;
			}

			this.showError('');
			this.setLoading(true);

			try {
				const drawer = this.cartDrawer;

				// getSectionsToRender only exists once cart-drawer.js has upgraded the element.
				const canRenderDrawer = Boolean(drawer) && typeof drawer.getSectionsToRender === 'function';
				const body = { items };

				if (canRenderDrawer && !goToCheckout) {
					body.sections = drawer.getSectionsToRender().map((section) => section.id);
					body.sections_url = window.location.pathname;
				}

				const response = await fetch(this.dataset.cartAddUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/javascript',
					},
					body: JSON.stringify(body),
				});
				const data = await response.json();

				if (!response.ok || data.status) {
					this.showError(data.description || data.message || (window.cartStrings ? window.cartStrings.error : ''));
					return;
				}

				if (goToCheckout) {
					window.location.href = this.dataset.checkoutUrl;
					return;
				}

				if (canRenderDrawer && data.sections) {
					drawer.renderContents(data);
				} else {
					window.location.href = this.dataset.cartUrl;
					return;
				}

				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nodus-product-form' });
				}
			} catch (error) {
				console.error('Nodus product form error:', error);
				this.showError(window.cartStrings ? window.cartStrings.error : '');
			} finally {
				this.setLoading(false);
			}
		}
	}

	customElements.define('nodus-product-form', NodusProductForm);
}
